import { MarketplacePartnerTierType } from '@atlassian/mpac-types/graphql-gateway-types';

import type { BundleConfig } from './bundleConfig';

import DevopsBundleHeader from '../assets/devops/devops_bundle_header.svg';
import FooterLeftImage from '../assets/devops/devops_footer_left_image.png';
import FooterRightImage from '../assets/devops/devops_footer_right_image.png';
import Opportunity1Icon from '../assets/devops/devops_opportunity1_icon.svg';
import Opportunity2Icon from '../assets/devops/devops_opportunity2_icon.svg';
import Opportunity3Icon from '../assets/devops/devops_opportunity3_icon.svg';
import Opportunity4Icon from '../assets/devops/devops_opportunity4_icon.svg';
import DevopsBundleHero from '../assets/devops_bundle_tile.png';

const DEVOPS_APPS = [
  {
    title: 'Git Integration for Jira (Azure DevOps, GitHub, GitLab)',
    description: 'Code traceability & integration',
    icon: 'https://marketplace.atlassian.com/product-listing/files/7425dc5d-261d-43d3-b810-91a1944a8b3c?fileType=image&mode=full-fit',
    iconAlt: 'Git Integration for Jira Icon',
    url: 'https://marketplace.atlassian.com/apps/4984/git-integration-for-jira-azure-devops-github-gitlab',
  },
  {
    title: 'Release Management: Workflows, Calendar & Roadmaps - Cloud',
    description: 'Release planning & tracking',
    icon: 'https://marketplace.atlassian.com/product-listing/files/a55e4c7e-1067-49da-9a0d-e2d32df1cf4e?fileType=image&mode=full-fit',
    iconAlt: 'Release Management Icon',
    url: 'https://marketplace.atlassian.com/apps/1221946/release-management-workflows-calendar-roadmaps-cloud',
  },
  {
    title: 'TFS4JIRA Azure DevOps integration',
    description: 'Azure DevOps Sync',
    icon: 'https://marketplace.atlassian.com/product-listing/files/88ed783b-759b-4079-9888-a3ce51c9df87?fileType=image&mode=full-fit',
    iconAlt: 'TFS4JIRA Azure DevOps Icon',
    url: 'https://marketplace.atlassian.com/apps/42296/tfs4jira-azure-devops-integration',
  },
];

const DEVOPS_BENEFITS = {
  title: 'Unblock your DevOps pipeline for faster delivery',
  description:
    'DevOps challenges can slow deployment cycles, increase errors, and reduce team efficiency.',
  items: [
    {
      title: 'Limited visibility into development progress',
      description:
        'Teams lack real-time insight into code activity and task status, making it hard to detect risks or blockers early.',
      icon: Opportunity1Icon,
      iconAlt: 'Visibility Icon',
    },
    {
      title: 'Inefficient release planning',
      description:
        'Without centralized workflows and shared timelines, coordinating releases across teams becomes error-prone and slow.',
      icon: Opportunity2Icon,
      iconAlt: 'Incident Resolution Icon',
    },
    {
      title: 'Poor traceability across the delivery pipeline',
      description:
        'Tracking work from initial planning through deployment is difficult, impacting auditability, accountability, and quality.',
      icon: Opportunity3Icon,
      iconAlt: 'Toolchain Icon',
    },
    {
      title: 'Disconnected tooling across platforms',
      description:
        'Lack of seamless integration between planning and development tools leads to duplication, silos, and misaligned execution.',
      icon: Opportunity4Icon,
      iconAlt: 'Workflows Icon',
    },
  ],
};

const getDevopsAppsSections = (terminology: string) => [
  {
    title: 'Enhance code traceability and team collaboration',
    description: `Link commits and pull requests to Jira ${terminology} for better visibility and traceability, improving communication and coordination across your development and operations teams.`,
    appDetails: {
      title: 'Git Integration for Jira (Azure DevOps, GitHub, GitLab)',
      vendor: 'by GitKraken',
      partnerTier: MarketplacePartnerTierType.Platinum,
      rating: '3.5/4',
      reviewCount: '337',
      installOrDownloadsText: '10.8k installs',
      badge: 'cloud-fortified' as const,
    },
    videoId: 'IRyiQCx7buU',
  },
  {
    title: 'Plan, visualize, and track releases',
    description:
      'Create workflows, timelines, and calendars to manage release phases, assign owners, and keep teams aligned.',
    appDetails: {
      title: 'Release Management: Workflows, Calendar & Roadmaps - Cloud',
      vendor: 'by Release Management',
      partnerTier: MarketplacePartnerTierType.Platinum,
      rating: '3.4/4',
      reviewCount: '34',
      installOrDownloadsText: '1k installs',
      badge: 'cloud-fortified' as const,
    },
    videoId: 'Utt7vU3jk9s',
  },
  {
    title: 'Bridge Jira and Azure DevOps seamlessly',
    description:
      'Sync work items, status, and comments bidirectionally, ensuring consistent data across platforms for smoother execution.',
    appDetails: {
      title: 'TFS4JIRA Azure DevOps integration',
      vendor: 'by Appfire',
      partnerTier: MarketplacePartnerTierType.Platinum,
      rating: '3.7/4',
      reviewCount: '224',
      installOrDownloadsText: '2k installs',
      badge: 'cloud-fortified' as const,
    },
    videoId: 'aT9RpXqde0c',
  },
];

export function devopsBundleConfig(enableIssueTerminology: boolean): BundleConfig {
  const terminology = enableIssueTerminology ? 'work items' : 'issues';
  return {
    hero: {
      breadcrumbTitle: 'DevOps Bundle',
      title: 'DevOps Flow Essentials',
      description:
        'Streamline DevOps workflows with synced planning, development visibility, and integrated toolchains.',
      requestButton: 'Request now',
      headerImage: DevopsBundleHeader,
      headerImageAlt: 'DevOps Bundle Header Image',
      heroImage: DevopsBundleHero,
      heroImageAlt: 'DevOps Bundle Hero Image',
    },
    apps: DEVOPS_APPS,
    benefits: DEVOPS_BENEFITS,
    appsSection: {
      title: 'An easier way to work better together',
      description:
        'Connect every stage of your DevOps workflow. Simplify handoffs, reduce friction, and accelerate delivery through seamless integration.',
      sections: getDevopsAppsSections(terminology),
    },
    footer: {
      title: 'Ready to evolve your DevOps team?',
      description: 'Simplify processes, improve teamwork, and deliver quality software faster.',
      requestButton: 'Request now',
      leftImageAlt: 'Team collaboration left',
      rightImageAlt: 'Team collaboration right',
      leftImage: FooterLeftImage,
      rightImage: FooterRightImage,
    },
  };
}
